import type { Education as EducationType } from '@seek/ca-myprofile-utils';
import { EducationForm, VerifiedEducationForm } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Alert, Stack, Text } from 'braid-design-system';
import type { ComponentProps } from 'react';

import type { Qualification } from 'src/__generated__/graphql';
import { useConfig } from 'src/context/config';
import { logError } from 'src/modules/error-logger/browserLog';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';
import { useEducationMutations } from './hooks/useEducationMutations';
import { useTracking } from './hooks/useTracking';

type SubmitHandler = ComponentProps<typeof EducationForm>['onSubmit'];
interface Props {
  qualifications: Qualification[];
  unconfirmedQualifications: Qualification[];
  shouldCloseDrawerOnExit?: boolean;
}

export function EducationEditor({
  unconfirmedQualifications,
  qualifications,
  shouldCloseDrawerOnExit = false,
}: Props) {
  const { config } = useConfig();
  const { t } = useTranslations(translations);
  const { editingId, onEditorClose, onFormDirty, switchToListView } =
    useEditorDrawer();
  const allQualifications = [...unconfirmedQualifications, ...qualifications];
  const confirmedIdsSet = new Set(qualifications.map((q) => q.id));
  const unconfirmedIdsSet = new Set(unconfirmedQualifications.map((q) => q.id));
  const isDeletable =
    editingId &&
    (confirmedIdsSet.has(editingId) || unconfirmedIdsSet.has(editingId)) &&
    allQualifications.length > 1;
  const defaultValues = allQualifications.find(({ id }) => id === editingId) as
    | EducationType
    | undefined;

  const {
    deleteQualification,
    rejectQualification,
    confirmQualification,
    updateQualification,
    error,
    reset,
  } = useEducationMutations();
  const {
    trackRejectQualification,
    trackDeleteQualification,
    trackDeleteVerifiedQualification,
    trackEditQualification,
    trackAddQualification,
  } = useTracking({ qualifications, editingId });

  const onExit = shouldCloseDrawerOnExit ? onEditorClose : switchToListView;

  const handleDelete = async () => {
    if (!editingId) return;

    const values = qualifications.find((q) => q.id === editingId);

    try {
      if (unconfirmedIdsSet.has(editingId)) {
        await rejectQualification(editingId);
        trackRejectQualification();
      } else if (values) {
        await deleteQualification(editingId);
        trackDeleteQualification();
      }

      onExit();
      reset();
    } catch (err: any) {
      logError({ ...err, type: 'delete-qualification' }, { error: err });
    }
  };

  const handleSubmit: SubmitHandler = async ({ values }) => {
    if (!editingId) return;

    try {
      if (unconfirmedIdsSet.has(editingId)) {
        await confirmQualification(values);

        trackEditQualification('cv parsed');
      } else {
        await updateQualification(editingId, values);

        if (confirmedIdsSet.has(editingId)) {
          trackEditQualification();
        } else {
          trackAddQualification();
        }
      }

      onExit();
      reset();
    } catch (err: any) {
      logError({ ...err, type: 'update-qualification' }, { error: err });
    }
  };

  if (defaultValues?.credential) {
    return (
      <VerifiedEducationForm
        name={defaultValues.name.text}
        issuingOrganisation={defaultValues.institute.text}
        credentialInfo={defaultValues.credential.credentialInfo}
        status={defaultValues.status ?? null}
        onDelete={() => {
          const dest = defaultValues.credential?.deleteVerificationUrl;
          if (!dest) return;

          window.open(dest, '_blank', 'noopener,noreferrer');
          trackDeleteVerifiedQualification();
        }}
        onCancel={onExit}
        readonly
      />
    );
  }
  if (editingId && !defaultValues?.credential) {
    return (
      <Stack space="large">
        <EducationForm
          zone={config.ZONE}
          initialValues={defaultValues}
          // TODO: values.completionDate should not be a number according to the type
          // temp fixed in useEducationMutations
          onSubmit={handleSubmit}
          onCancel={onExit}
          onDelete={isDeletable ? handleDelete : undefined}
          onFormDirtyChange={onFormDirty}
          scrollToTopOnMobile={false}
        />
        {error && (
          <Alert tone="critical" data={{ automation: 'education-error' }}>
            <Text>{t('Sorry we couldnt')}</Text>
          </Alert>
        )}
      </Stack>
    );
  }
  return null;
}
